/**
 * S21c. The welcome. `/welcome/[slug]`
 *
 * "Same app, now yours." Four words, and every pixel under them is evidence
 * for one of the two halves.
 *
 * THE WEB ADDRESS IS THE HEADLINE'S PROOF, so it is printed unchanged, in the
 * copy field, at the largest size the atom has, directly under the sentence
 * that claims nothing moved. It is the same literal string the claim screen
 * showed before there was an account and the same literal string the overview
 * shows afterwards. A person who screenshots this page and compares it to a
 * screenshot from last month finds the two identical, which is a stronger
 * argument than any sentence this screen could write.
 *
 * THE LEDGER IS TWO COLUMNS BECAUSE THE QUESTION HAS TWO HALVES. "What
 * changed" is short, specific and numeric: the owner, the plan, three limits,
 * and the removal date that no longer exists. "What did not" is longer, and it
 * is longer on purpose, because that asymmetry IS the message. The right hand
 * column being the taller one is the whole screen in one glance, before a word
 * of it has been read.
 *
 * The two columns are shaped differently rather than mirrored. Changes are
 * facts with values, so they render as key and value rows. Non-changes are
 * reassurances, so they render as the kit's consequence list, every item a
 * survival with a check. Two lists of identical rows would have read as a
 * comparison table, and a comparison table invites you to look for the losing
 * column. There isn't one.
 *
 * DEVIATION FROM SCREEN SPEC S21c, recorded. The spec puts this panel above a
 * fully populated S7 overview inside the app shell, and hangs the changed
 * versus did-not block behind Advanced. This route renders outside the shell,
 * which is where the router already had it, and the ledger is in Simple. The
 * reason is that the ledger is not an Advanced fact here, it is the answer to
 * the only question this screen exists to answer, and hiding it behind a
 * switch would leave Simple with a claim and no evidence. The payoff is not
 * lost: the ink block goes straight into the real overview, which is exactly
 * one click and a fully populated project.
 *
 * The plan's limits are read from the usage record rather than typed, so the
 * three numbers on this page and the three bars on the usage screen can never
 * disagree. A welcome screen that quoted a limit the product then measured
 * differently would poison the one page whose only job is to be believed.
 *
 * ONE INK BLOCK: "Go to your project". There is exactly one thing to do after
 * reading this, and every other control on the page is a link or a copy
 * control.
 *
 * States:
 *   /welcome/soi-cats             the payoff
 *   ?state=loading                the skeletons, held open
 * A slug that names no project renders the same not-here notice the rest of
 * the product uses, rather than a blank page.
 */

import { useEffect } from 'react';
import { Link, useParams } from 'react-router';

import { Button } from '../../components/primitives/Button';

import { ConsequenceList } from '../../components/patterns/ConsequenceList';
import type { ConsequenceItem } from '../../components/patterns/ConsequenceList';
import { CopyField } from '../../components/patterns/CopyField';
import { HeroBackdrop } from '../../components/patterns/HeroBackdrop';
import { Notice } from '../../components/patterns/Notice';
import { Skeleton } from '../../components/patterns/Skeleton';
import { StatusPill } from '../../components/patterns/StatusPill';
import { VisitLink } from '../../components/patterns/VisitLink';
import { Wordmark } from '../../components/brand/Wordmark';

import { environmentOf, getProject, getUsage } from '../../lib/api';
import { bytes, count } from '../../lib/format';
import { useAsync } from '../../lib/hooks/use-async';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { t } from '../../lib/i18n/t';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { UsageMetric } from '../../lib/types';

import '../../styles/screens/graduation.css';

const STATES = ['resting', 'loading'] as const;
type StateName = (typeof STATES)[number];

const MEASURE: Record<UsageMetric['unit'], (n: number) => string> = {
  bytes,
  count,
  minutes: count,
};

/* ------------------------------------------------------------------ screen */

export default function Welcome() {
  // This route sits outside the project shell, so the slug comes from its own
  // param rather than from useProjectSlug, which reads /p/:slug and correctly
  // returns null here.
  const { slug = '' } = useParams();

  const scenarioId = useDemoStore((s) => s.scenarioId);
  const { state } = useScreenState<StateName>(STATES, 'resting');

  const { data, isLoading } = useAsync(async () => {
    const project = await getProject(slug);
    if (!project) return null;
    const usage = await getUsage(project.id);
    return { project, usage };
  }, [slug, scenarioId]);

  useEffect(() => {
    if (data?.project) {
      captureEvent('graduation_welcome_viewed', {
        project_slug: data.project.slug,
        project_id: data.project.id,
      });
    }
  }, [data?.project]);

  if (isLoading || state === 'loading') {
    return (
      <div className="grad">
        <HeroBackdrop reach="tight" />
        <div className="grad__lockup">
          <Wordmark size={32} />
        </div>
        <Skeleton shape="panel" count={2} label={t('Opening your project')} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="grad">
        <HeroBackdrop reach="tight" />
        <div className="grad__lockup">
          <Wordmark size={32} />
        </div>
        <Notice tone="quiet" title={t('That project is not here.')}>
          <p>{t('It may have been deleted, or the address may have a typo in it.')}</p>
        </Notice>
        <p className="grad__foot">
          <Link className="link-inline" to="/projects">
            {t('All projects')}
          </Link>
        </p>
      </div>
    );
  }

  const { project, usage } = data;
  const address = environmentOf(project).webAddress;
  const host = address.replace(/^https?:\/\//, '');

  const limitOf = (resource: UsageMetric['resource']): string => {
    const metric = usage?.metrics.find((one) => one.resource === resource);
    if (!metric) return '';
    return MEASURE[metric.unit](metric.limit);
  };

  /**
   * Six rows, and the last one is the one that matters most.
   *
   * A removal date that used to be on this project and is now gone is not an
   * administrative fact. It is the thing the reminder emails were about, and
   * printing it as a cleared row is how the person finds out the countdown
   * they have been living with has stopped.
   */
  const changed: { key: string; value: string }[] = [
    { key: t('Owner'), value: t('You') },
    { key: t('Compute Usage'), value: t('{n} a month', { n: limitOf('compute') }) },
    { key: t('Database'), value: limitOf('database-storage') },
    { key: t('Files'), value: limitOf('file-storage') },
    { key: t('Cleanup date'), value: t('None') },
  ];

  const unchanged: ConsequenceItem[] = [
    { kind: 'survives', text: t('{host}, the same address', { host }) },
    { kind: 'survives', text: t('Your database and everything in it') },
    { kind: 'survives', text: t('Your files') },
    { kind: 'survives', text: t('Your keys and settings') },
    { kind: 'survives', text: t('Your GitHub repository') },
    { kind: 'survives', text: t('Your deploy history') },
    { kind: 'survives', text: t('The version that is running right now') },
  ];

  return (
    <div className="grad">
      <HeroBackdrop reach="tight" />

      <div className="grad__lockup">
        <Wordmark size={32} />
      </div>

      <header className="grad__head">
        <h1 className="grad__title">{t('Same app, now yours.')}</h1>
        <p className="grad__say">{t('Same web address. Same data. Nothing to move.')}</p>
      </header>

      <section className="gcard" aria-label={t('Your project')}>
        <div className="gcard__head">
          <h2 className="gcard__name">{project.name}</h2>
          <StatusPill value={project.status === 'paused' ? 'paused' : 'live'} />
        </div>

        {/* The host, not the full URL. Same literal, same size, same place as
            the claim screen, so the two screens print one string twice rather
            than two strings that look alike. */}
        <CopyField label={t('Web address')} value={host} size="lg" />

        <div className="gcard__foot">
          <p className="gcard__deploy">{t('It has been answering here the whole time.')}</p>
          <VisitLink href={address} label={t('Visit')} />
        </div>
      </section>

      <div className="gledger">
        <section className="gledger__col" aria-label={t('What changed')}>
          <h2 className="gledger__title">{t('What changed')}</h2>
          <dl className="gledger__rows">
            {changed.map((row) => (
              <div className="gledger__row" key={row.key}>
                <dt className="gledger__key">{row.key}</dt>
                <dd className="gledger__value mono">{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="gledger__col" aria-label={t('What did not change')}>
          <h2 className="gledger__title">{t('What did not change')}</h2>
          <ConsequenceList items={unchanged} label={t('What did not change')} />
        </section>
      </div>

      <div className="grad__wide">
        <Link to={`/p/${encodeURIComponent(project.slug)}`}>
          <Button variant="solid">{t('Go to your project')}</Button>
        </Link>
      </div>

      <p className="grad__foot">
        {t('Every time you push to main, this updates automatically. That has not changed either.')}
      </p>
    </div>
  );
}
